import { useState, useEffect, useRef, useCallback } from 'react';

export type SpeechStatus = 'idle' | 'speaking' | 'paused';

interface UseSpeechSynthesisOptions {
  /** BCP-47 language tag (default: 'es-UY') */
  lang?: string;
  /** Speech rate 0.1 – 10 (default: 1) */
  rate?: number;
  /** Pitch 0 – 2 (default: 1) */
  pitch?: number;
}

/**
 * Strip Markdown syntax so it's not read aloud.
 */
function stripMarkdown(md: string): string {
  return md
    .replace(/#{1,6}\s+/g, '')          // headings
    .replace(/\*\*(.+?)\*\*/g, '$1')    // bold
    .replace(/__(.+?)__/g, '$1')         // bold alt
    .replace(/\*(.+?)\*/g, '$1')        // italic
    .replace(/_(.+?)_/g, '$1')          // italic alt
    .replace(/~~(.+?)~~/g, '$1')        // strikethrough
    .replace(/`{1,3}[^`]*`{1,3}/g, '')  // inline code / code blocks
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links – keep text
    .replace(/!\[.*?\]\(.*?\)/g, '')     // images
    .replace(/^>\s+/gm, '')             // blockquotes
    .replace(/^[-*+]\s+/gm, '')         // unordered lists
    .replace(/^\d+\.\s+/gm, '')         // ordered lists
    .replace(/---+/g, '')               // horizontal rules
    .replace(/\n{3,}/g, '\n\n')         // collapse extra newlines
    .trim();
}

/**
 * Chrome has a ~15 s silence bug on long utterances. To work around it we
 * split the text into chunks at sentence boundaries and queue them one by
 * one, resuming speech automatically after each chunk ends.
 */
function splitIntoChunks(text: string, maxLen = 200): string[] {
  const sentences = text.match(/[^.!?…\n]+[.!?…\n]*/g) || [text];
  const chunks: string[] = [];
  let current = '';

  for (const sentence of sentences) {
    if ((current + sentence).length > maxLen && current.length > 0) {
      chunks.push(current.trim());
      current = sentence;
    } else {
      current += sentence;
    }
  }
  if (current.trim()) chunks.push(current.trim());
  return chunks;
}

export function useSpeechSynthesis(
  text: string,
  options: UseSpeechSynthesisOptions = {},
) {
  const { lang = 'es-UY', rate = 1, pitch = 1 } = options;

  const [status, setStatus] = useState<SpeechStatus>('idle');
  const [progress, setProgress] = useState(0); // 0..1
  const [supported, setSupported] = useState(true);

  const chunksRef = useRef<string[]>([]);
  const indexRef = useRef(0);
  const totalCharsRef = useRef(0);
  const spokenCharsRef = useRef(0);

  // Check browser support
  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      setSupported(false);
    }
  }, []);

  // Cancel on unmount or when text changes
  useEffect(() => {
    return () => {
      window.speechSynthesis?.cancel();
      setStatus('idle');
      setProgress(0);
    };
  }, [text]);

  const speakChunk = useCallback(
    (index: number) => {
      const chunks = chunksRef.current;
      if (index >= chunks.length) {
        setStatus('idle');
        setProgress(1);
        return;
      }

      const utterance = new SpeechSynthesisUtterance(chunks[index]);
      utterance.lang = lang;
      utterance.rate = rate;
      utterance.pitch = pitch;

      utterance.onend = () => {
        spokenCharsRef.current += chunks[index].length;
        setProgress(spokenCharsRef.current / totalCharsRef.current);
        indexRef.current = index + 1;
        speakChunk(index + 1);
      };

      utterance.onerror = (e) => {
        // 'interrupted' and 'canceled' are expected when the user stops/pauses
        if (e.error !== 'interrupted' && e.error !== 'canceled') {
          console.error('SpeechSynthesis error:', e.error);
        }
        setStatus('idle');
      };

      window.speechSynthesis.speak(utterance);
    },
    [lang, rate, pitch],
  );

  const speak = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();

    const plain = stripMarkdown(text);
    const chunks = splitIntoChunks(plain);
    chunksRef.current = chunks;
    indexRef.current = 0;
    totalCharsRef.current = plain.length || 1;
    spokenCharsRef.current = 0;

    setStatus('speaking');
    setProgress(0);
    speakChunk(0);
  }, [supported, text, speakChunk]);

  const pause = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.pause();
    setStatus('paused');
  }, [supported]);

  const resume = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.resume();
    setStatus('speaking');
  }, [supported]);

  const stop = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setStatus('idle');
    setProgress(0);
    indexRef.current = 0;
    spokenCharsRef.current = 0;
  }, [supported]);

  return { status, progress, supported, speak, pause, resume, stop };
}
